// A debounced callback on the product timer module (#12187), for the
// autosave / search-as-you-type shape: the last call within `ms` wins, and
// the pending call is cancelled when the component unmounts. Built on
// `setProductTimeout`, so a story drives the debounce with `withProductClock`
// rather than typing against a real 80 ms window.

import { useCallback, useEffect, useMemo, useRef } from "react";
import {
  clearProductTimeout,
  setProductTimeout,
  type ProductTimer,
} from "@/utils/productTimers";

export interface DebouncedCallback<Args extends unknown[]> {
  /** Schedule `callback(...args)` after `ms`, replacing any pending call. */
  (...args: Args): void;
  /** Drop the pending call, if any. */
  cancel: () => void;
  /** Run the pending call now, if any. */
  flush: () => void;
}

export const useDebouncedCallback = <Args extends unknown[]>(
  callback: (...args: Args) => void,
  ms: number
): DebouncedCallback<Args> => {
  const callbackRef = useRef(callback);
  callbackRef.current = callback;
  const timerRef = useRef<ProductTimer | null>(null);
  const pendingRef = useRef<Args | null>(null);

  const cancel = useCallback(() => {
    clearProductTimeout(timerRef.current);
    timerRef.current = null;
    pendingRef.current = null;
  }, []);

  const flush = useCallback(() => {
    const args = pendingRef.current;
    cancel();
    if (args) callbackRef.current(...args);
  }, [cancel]);

  useEffect(() => cancel, [cancel]);

  return useMemo(() => {
    const debounced = (...args: Args): void => {
      clearProductTimeout(timerRef.current);
      pendingRef.current = args;
      timerRef.current = setProductTimeout(() => {
        timerRef.current = null;
        pendingRef.current = null;
        callbackRef.current(...args);
      }, ms);
    };
    return Object.assign(debounced, { cancel, flush });
  }, [ms, cancel, flush]);
};
